import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#reopenIfNeeded);
    document.addEventListener('cart:update', this.#syncEmptyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Detect when a user clicks delete or changes quantity
    this.addEventListener('click', this.#handleCartInteraction);
    this.addEventListener('change', this.#handleCartInteraction);

    // If the entire component was just morphed back into the DOM, force reopen!
    if (window.cartDrawerWasOpen) {
      requestAnimationFrame(() => {
        const dialog = this.querySelector('dialog[ref="dialog"]');
        if (dialog && !dialog.open) {
          if ('dialog' in this) this.dialog = dialog; // Fix parent cache
          try { this.showDialog(); } catch(e) { try { dialog.showModal(); } catch(e2) { dialog.setAttribute('open', ''); } }
        }
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#reopenIfNeeded);
    document.removeEventListener('cart:update', this.#syncEmptyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleCartInteraction);
    this.removeEventListener('change', this.#handleCartInteraction);
  }

  // ---------------------------------------------------------------------
  // ROOT-CAUSE FIX
  // ---------------------------------------------------------------------
  // Confirmed via logging: while a delete/quantity click is being processed,
  // something inside DialogComponent's own click-outside / escape handling
  // calls closeDialog(). closeDialog() calls the REAL native dialog.close()
  // *before* it dispatches DialogCloseEvent. That means by the time our
  // window.cartIsUpdating flag is checked inside #handleDialogClose below,
  // the drawer has already visibly closed - the flag only stops us from
  // mis-tracking that as a "real" user close afterward, it does nothing to
  // stop the close itself. #reopenIfNeeded then genuinely reopens it a
  // moment later, which is the close-then-reopen flicker.
  //
  // The fix: make closeDialog() itself a no-op whenever a cart action is in
  // flight, so the native dialog never actually closes in the first place -
  // regardless of which internal DialogComponent path tried to trigger it.
  //
  // closeDialog is defined in DialogComponent as an arrow-function class
  // field (not a prototype method), so `super.closeDialog()` isn't
  // reachable from here. Instead we capture DialogComponent's version (it
  // was already assigned to `this.closeDialog` moments earlier, during the
  // parent class's field initialization) and wrap it.
  closeDialog = (() => {
    const baseCloseDialog = this.closeDialog; // DialogComponent's original implementation
    return async () => {
      if (window.cartIsUpdating) return;
      return baseCloseDialog();
    };
  })();

  // Since the dialog now has data-skip-node-update (so morph never touches its
  // attributes and can't flicker it closed), we need to manually keep the
  // "empty cart" styling class in sync ourselves instead of relying on morph.
  #syncEmptyState = (event) => {
    const itemCount = event.detail?.data?.itemCount;
    if (typeof itemCount !== 'number') return;

    const dialog = this.querySelector('dialog[ref="dialog"]');
    dialog?.classList.toggle('cart-drawer--empty', itemCount === 0);
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  // 1. We intercept the click on the delete/quantity buttons
  #handleCartInteraction = (e) => {
    const isCartAction = e.target.closest('quantity-input, a[href*="/cart/change"], [data-remove], [data-action="remove"], input[name="updates[]"], .cart-items__remove, [on\\:click*="onLineItemRemove"]');
    if (isCartAction) {
      // Tell the drawer: "Hey, a cart update is happening! Don't close!"
      window.cartIsUpdating = true;
    }
  };

  #handleDialogOpen = () => {
    this.dataset.wasOpen = 'true';
    window.cartDrawerWasOpen = true;
  };

  // 2. We ignore the fake close event fired by the morph
  #handleDialogClose = () => {
    // If the cart is updating, the morph triggered a fake close event. DO NOTHING.
    if (window.cartIsUpdating) return;

    // Otherwise, it was a real user close (like clicking the X).
    const liveDrawer = document.querySelector('cart-drawer-component');
    const dialog = liveDrawer?.querySelector('dialog[ref="dialog"]');
    if (!dialog || !dialog.open) {
      if (liveDrawer) delete liveDrawer.dataset.wasOpen;
      window.cartDrawerWasOpen = false;
    }
  };

  // 3. We force the drawer back open using the LIVE dialog element
  #reopenIfNeeded = () => {
    // IMPORTANT: don't clear window.cartIsUpdating synchronously here.
    //
    // This handler runs as soon as CartUpdateEvent is dispatched, which happens
    // BEFORE morphSection() runs (morphSection has to run second, since dispatching
    // the event requires the cart-items-component to still be attached to the
    // document so it can bubble up to us - see the comment in cart-items.js).
    //
    // That means if we clear the flag right away, it's already `false` by the time
    // morphSection() actually runs and fires its "fake" dialog close event a moment
    // later - so #handleDialogClose no longer knows to ignore it, and mistakes it
    // for a real user close.
    //
    // Deferring the reset (and the reopen check) to the next animation frame lets
    // morphSection() and its fake close event finish first, while cartIsUpdating
    // is still true, so that fake close gets correctly ignored. Only afterwards do
    // we clear the flag and check whether we need to force the (newly morphed)
    // dialog back open.
    requestAnimationFrame(() => {
      window.cartIsUpdating = false;

      if (this.dataset.wasOpen !== 'true' && !window.cartDrawerWasOpen) return;

      const liveDrawer = document.querySelector('cart-drawer-component');
      const dialog = liveDrawer?.querySelector('dialog[ref="dialog"]');

      if (liveDrawer && dialog && !dialog.open) {
        // CRITICAL FIX: Force the parent class to stop looking at the ghost dialog!
        if ('dialog' in liveDrawer) {
          liveDrawer.dialog = dialog;
        }

        // Call the theme's official showDialog method
        try {
          liveDrawer.showDialog();
        } catch (e) {
          try { dialog.showModal(); } catch(e2) { dialog.setAttribute('open', ''); }
        }

        // Fallback: If showDialog didn't actually open the live dialog (because the
        // parent class uses a private field we can't access), force it open natively.
        if (!dialog.open) {
          try { dialog.showModal(); } catch(e) { dialog.setAttribute('open', ''); }
        }
      }
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}